import { Controller } from "stimulus"

// Formats a numeric text input as the user types, without disrupting the flow:
// it strips non-numeric characters, keeps a single decimal point, and — when
// grouping is enabled — inserts thousands separators (1,000 / 10,000). The caret
// is preserved by counting the digits to the left of it before reformatting and
// landing it after the same digit afterward, so editing the middle of a number
// doesn't bounce the cursor to the end.
//
// The currency "$" and percent "%" markers are visual adornments in the markup
// (absolutely-positioned spans), NOT part of the value — so the field posts only
// digits, the dot, and commas. The server strips the commas before coercing
// (DebtQuests::DebtTracker::DebtsController#coerce_numeric_fields), so the
// formatted value is always safe to submit.
//
// Usage:
//   data-controller="formatted-number"                      # numbers only
//   data-controller="formatted-number" data-formatted-number-group-value="true"  # + thousands separators
//   data-controller="formatted-number" data-formatted-number-max-integer-digits-value="7"
//   data-action="input->formatted-number#format"
export default class extends Controller {
  static get values() {
    // Stimulus 2 has no per-value defaults — omit maxIntegerDigits to leave unlimited.
    return { group: Boolean, maxIntegerDigits: Number }
  }

  connect() {
    // Normalize a pre-filled value (e.g. an edit form's stored amount, which
    // renders as "1000.0") to the display format without needing a keystroke.
    this._normalizeInitial()
  }

  format() {
    const el = this.element
    const original = el.value
    const caret = el.selectionStart == null ? original.length : el.selectionStart
    const digitsLeft = this._countDigits(original.slice(0, caret))

    const formatted = this._formatString(original)
    if (formatted === original) return

    el.value = formatted
    const newCaret = this._caretAfterDigits(formatted, digitsLeft)
    // Number inputs throw on setSelectionRange; type="text" here, but guard anyway.
    try { el.setSelectionRange(newCaret, newCaret) } catch (_e) { /* noop */ }
  }

  _normalizeInitial() {
    const raw = this.element.value
    if (!raw.trim()) return
    const n = Number(this._clean(raw))
    if (!Number.isFinite(n)) return
    this.element.value = this.groupValue
      ? n.toLocaleString("en-US", { maximumFractionDigits: 2 })
      : String(n)
  }

  // Keep only digits and a single (leading) decimal point. When
  // maxIntegerDigits is set, truncate the integer part so the user cannot
  // type or paste more digits than allowed (decimals are unaffected).
  _clean(value) {
    let cleaned = (value || "").replace(/[^0-9.]/g, "")
    const firstDot = cleaned.indexOf(".")
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "")
    }
    return this._limitIntegerDigits(cleaned)
  }

  _limitIntegerDigits(cleaned) {
    const max = this.maxIntegerDigitsValue
    if (!max || max <= 0) return cleaned

    const [intPart, ...rest] = cleaned.split(".")
    if (intPart.length <= max) return cleaned

    const limited = intPart.slice(0, max)
    return rest.length ? `${limited}.${rest.join("")}` : limited
  }

  // Group the integer part with commas when enabled, preserving a trailing dot
  // and any partial decimals so an in-progress entry like "1000." isn't cut off.
  _formatString(value) {
    const cleaned = this._clean(value)
    if (!this.groupValue) return cleaned
    const [intPart, ...rest] = cleaned.split(".")
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return rest.length ? `${grouped}.${rest.join("")}` : grouped
  }

  _countDigits(str) {
    return (str.match(/\d/g) || []).length
  }

  // Position just after the Nth digit in the formatted string (commas skipped).
  _caretAfterDigits(formatted, digitsLeft) {
    if (digitsLeft <= 0) return 0
    let seen = 0
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        seen++
        if (seen === digitsLeft) return i + 1
      }
    }
    return formatted.length
  }
}
